<template>
  <div class="flex h-screen app-bg overflow-hidden">
    <app-sidebar :brand="userType" />
    <div
      id="layout-content"
      class="flex flex-col flex-1 overflow-y-auto relative"
    >
      <div class="p-8 flex-grow flex flex-col">
        <router-view />
      </div>
      <global-terms-links
        v-if="!route.meta.hideTermsFooter"
        :sion-terms="userType"
      />
    </div>
    <router-view name="summary" />
    <global-drawers v-if="!drawersHidden" />
  </div>
  <session-timeout-modal />
</template>

<script setup>
import { computed, watch, onBeforeMount, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserActivity } from '@/composables/useUserActivity.js';
import { useAuthStore } from '@/stores/auth.js';
import { SionUserTypes } from '@/constants/sion.js';
import { HttpStatus } from '@/constants/http.js';
import { useSupplierById } from '@/services/suppliers/queries.js';
import { useAgencyById } from '@/services/agencies/queries.js';
import AppSidebar from '@/components/sidebar/Sidebar.vue';
import GlobalDrawers from '@/components/ui/GlobalDrawers.vue';
import GlobalTermsLinks from '@/components/airwallex/GlobalTermsLinks.vue';
import SessionTimeoutModal from '@/components/auth/SessionTimeoutModal.vue';

const route = useRoute();
const router = useRouter();
useUserActivity();

const authStore = useAuthStore();

const userType = computed(() => authStore.userType);
const isAgencyUser = computed(() => userType.value === SionUserTypes.AGENCY);

const drawersHidden = computed(() => route.meta.drawersHidden ?? false);

/**
 * Set favicon and body class based on user type.
 */
onBeforeMount(() => {
  if (isAgencyUser.value) {
    document.getElementById('favicon').setAttribute('href', '/favicon-agency.ico');
    document.body.classList.add('is-agency-user');
  }
});

onUnmounted(() => {
  document.body.classList.remove('is-agency-user');
});

/**
 * Watches for supplier/agency fetch errors and redirects the user to the "account-maintenance" route
 * if the resource is unavailable or access is denied.
 *
 * Handles cases where:
 * - The user does not have access to the configured supplier/agency.
 * - The supplier/agency does not exist or is misconfigured (e.g., 404 or 422 errors).
 */
const errorCodes = [HttpStatus.UNPROCESSABLE_ENTITY, HttpStatus.NOT_FOUND];

// Supplier query - skip for agency users
const supplierId = computed(() => (isAgencyUser.value ? null : authStore.supplierId));
const { error: supplierError, isError: isSupplierError } = useSupplierById(supplierId, 0);

const supplierErrorStatus = computed(() => supplierError.value?.status);
const isSupplierUnavailable = computed(() => errorCodes.includes(supplierErrorStatus.value));

watch(isSupplierError, () => {
  if (isSupplierError.value && isSupplierUnavailable.value) {
    router.push({ name: 'account-maintenance' });
  }
});

// Agency query - skip for supplier users
const agencyId = computed(() => (isAgencyUser.value ? authStore.agencyId : null));
const { error: agencyError, isError: isAgencyError } = useAgencyById(agencyId, { enabled: true, retry: 0 });

const agencyErrorStatus = computed(() => agencyError.value?.status);
const isAgencyUnavailable = computed(() => errorCodes.includes(agencyErrorStatus.value));

watch(isAgencyError, () => {
  if (isAgencyError.value && isAgencyUnavailable.value) {
    router.push({ name: 'account-maintenance' });
  }
});
</script>
